import type {
  CompanyProfile,
  LineNotificationState,
  MeResponse,
} from "../types";
import { apiFetch, apiFetchForShopBootstrap } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

function normalizeLineNotificationState(
  value: unknown,
): LineNotificationState {
  if (value === null || typeof value !== "object") {
    throw new TypeError("LINE notification response is invalid");
  }
  const state = value as Omit<LineNotificationState, "recipients"> & {
    recipients: unknown;
  };
  if (!Array.isArray(state.recipients)) {
    throw new TypeError("LINE recipients response is invalid");
  }
  return {
    ...state,
    recipients: state.recipients.map((value) => {
      if (value === null || typeof value !== "object") {
        throw new TypeError("LINE recipient response is invalid");
      }
      const recipient = value as Omit<
        LineNotificationState["recipients"][number],
        "id"
      > & { id: unknown };
      return {
        ...recipient,
        id: canonicalStockyPublicBigIntId(recipient.id),
      };
    }),
  };
}

function normalizeMeResponse(value: unknown): MeResponse {
  if (value === null || typeof value !== "object") {
    throw new TypeError("me response is invalid");
  }
  const response = value as Omit<
    MeResponse,
    "shop_id" | "shopify_shop_id" | "line" | "locations"
  > & {
    shop_id: unknown;
    shopify_shop_id: unknown;
    line: unknown;
    locations: unknown;
  };
  if (response.locations === null || typeof response.locations !== "object") {
    throw new TypeError("me locations response is invalid");
  }
  const locations = response.locations as Omit<
    MeResponse["locations"],
    "primary_location_id"
  > & { primary_location_id: unknown };
  return {
    ...response,
    shop_id: canonicalStockyPublicBigIntId(response.shop_id),
    shopify_shop_id:
      response.shopify_shop_id === null
        ? null
        : canonicalStockyPublicBigIntId(response.shopify_shop_id),
    line: normalizeLineNotificationState(response.line),
    locations: {
      ...locations,
      primary_location_id:
        locations.primary_location_id === null
          ? null
          : canonicalStockyPublicBigIntId(locations.primary_location_id),
    },
  };
}

export async function getMe(): Promise<MeResponse> {
  return normalizeMeResponse(await apiFetch<unknown>("/api/me"));
}

/** Shop-generation-bound variant for workflows that can survive a shop switch. */
export async function getMeForShop(
  expectedShopDomain: string,
): Promise<MeResponse> {
  return normalizeMeResponse(
    await apiFetchForShopBootstrap<unknown>(expectedShopDomain),
  );
}

export interface ReferralProbeResponse {
  redirect_url: string;
}

export interface ReferralProbePayload {
  host: string;
  locale: string;
}

/** ref Cookie を回収するトップレベル referral probe を開始する。 */
export function startReferralProbe(
  body: ReferralProbePayload,
): Promise<ReferralProbeResponse> {
  return apiFetch("/api/me/referral-probe", { method: "POST", body });
}

export function updateCompanyProfile(
  body: CompanyProfile,
): Promise<CompanyProfile> {
  return apiFetch("/api/me/company-profile", { method: "PATCH", body });
}

export interface NotificationSettingsPayload {
  alert_email?: string | null;
  alert_slack_webhook_url?: string | null;
}

export function updateNotifications(
  body: NotificationSettingsPayload,
): Promise<NotificationSettingsPayload> {
  return apiFetch("/api/me/notifications", { method: "PATCH", body });
}

export interface ReceiptDefaultsPayload {
  apply_to_shopify: boolean;
}

/** 入荷登録の「Shopifyの在庫に反映する」の既定を店単位で保存する。 */
export function updateReceiptDefaults(
  body: ReceiptDefaultsPayload,
): Promise<ReceiptDefaultsPayload> {
  return apiFetch("/api/me/receipt-defaults", { method: "PATCH", body });
}

export interface NotificationChannelsPayload {
  alert_email_enabled?: boolean;
  alert_slack_enabled?: boolean;
  alert_line_enabled?: boolean;
}

/**
 * チャネルごとの送信オン/オフ。宛先は触らない。
 *
 * 未指定のキーは変更されない＝1つだけ送ってよい（宛先の PATCH とは意味が逆）。
 */
export function updateNotificationChannels(
  body: NotificationChannelsPayload,
): Promise<Required<NotificationChannelsPayload>> {
  return apiFetch("/api/me/notification-channels", { method: "PATCH", body });
}

/** LINE連携を全員分まとめて解除（連携コードは維持）。 */
export async function disconnectLine(): Promise<LineNotificationState> {
  return normalizeLineNotificationState(
    await apiFetch<unknown>("/api/me/line/disconnect", { method: "POST" }),
  );
}

/** 受信者を1人だけ解除する。 */
export async function removeLineRecipient(
  recipientId: StockyPublicBigIntId,
): Promise<LineNotificationState> {
  const canonicalRecipientId = canonicalStockyPublicBigIntId(recipientId);
  return normalizeLineNotificationState(
    await apiFetch<unknown>(
      `/api/me/line/recipients/${canonicalRecipientId}`,
      { method: "DELETE" },
    ),
  );
}

/** 連携コードを再発行する（漏れたコードを無効化する唯一の手段）。既存の連携は残る。 */
export async function rotateLineLinkCode(): Promise<LineNotificationState> {
  return normalizeLineNotificationState(
    await apiFetch<unknown>("/api/me/line/code", { method: "POST" }),
  );
}

/** 初回起動アンケートの流入元バケット（バックエンドの SurveySource と 1:1）。 */
export type SurveySource =
  | "app_store"
  | "search"
  | "blog"
  | "referral"
  | "other";

export interface SurveyAnswerPayload {
  source: SurveySource;
  /** 任意の紹介元テキスト（blog / referral のときのみ意味を持つ）。 */
  referrer?: string | null;
}

/** 初回起動アンケートを送信し、更新後の needs_survey を返す。 */
export function submitSurvey(
  body: SurveyAnswerPayload,
): Promise<{ needs_survey: boolean }> {
  return apiFetch("/api/me/survey", { method: "POST", body });
}
